import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../../../components/sidebar/Sidebar";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { Username } from "../../../utils/privacyMask";
import "../../accounts/create/CreateAccountPage.css";

export default function EditAccountPage() {

  const { id } = useParams();

  const [updated, setUpdated]     = useState(false);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [account, setAccount] = useState({
    username: "",
    email: "",
    telegram: "",
    telegramID: "",
    discord: "",
    discordID: "",
    discordPrivateChannelID: "",
    accountType: "customer",
    createdDate: null,
    updatedDate: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/account/${id}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        const a = res.data.account;
        setAccount({
          ...a,
          accountType: a.accountType || 'customer',
          createdDate: new Date(Number(a.createdDate)).getTime(),
          updatedDate: new Date(Number(a.updatedDate)).getTime(),
        });
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch account data");
      }
    };
    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'createdDate') {
      setAccount(prev => ({ ...prev, [name]: value ? new Date(value).getTime() : null }));
    } else {
      setAccount(prev => ({ ...prev, [name]: value }));
    }
  };

  const formatDateForInput = (unixMs) =>
    unixMs ? new Date(unixMs).toISOString().split('T')[0] : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setUpdated(false);
    setSubmitting(true);

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/account/update`,
        {
          _id: id,
          username: account.username,
          email: account.email,
          telegram: account.telegram,
          telegramID: account.telegramID,
          discord: account.discord,
          discordID: account.discordID,
          discordPrivateChannelID: account.discordPrivateChannelID,
          accountType: account.accountType || 'customer',
          createdDate: account.createdDate + "",
          updatedDate: new Date().getTime() + "",
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setUpdated(true);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update account");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error && !account.username) return (
    <div className="form-page">
      <div className="form-panel">
        <div className="form-panel-body">
          <div className="form-banner error">
            <i className="bi bi-exclamation-triangle-fill" />
            {error}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Sidebar title="Edit Account" />

      <div className="form-page">
        <div className="form-panel">

          {/* Header */}
          <div className="form-panel-header">
            <div>
              <div className="form-panel-title">Edit Account</div>
              <div className="form-panel-subtitle">
                Updating <strong style={{ color: '#e2e8f0' }}><Username value={account.username} /></strong>
                {account.updatedDate
                  ? ` — last saved ${new Date(account.updatedDate).toLocaleDateString()}`
                  : ''}
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="form-panel-body">
            <form onSubmit={handleSubmit} noValidate>

              {/* ── Identity ── */}
              <div className="form-section-label">Identity</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-person-badge" />
                      Username
                      <span className="required-star">*</span>
                    </label>
                    <input
                      className="form-input"
                      type="text"
                      name="username"
                      value={account.username}
                      onChange={handleChange}
                      placeholder="e.g. johndoe"
                      required
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-envelope" />
                      Email
                    </label>
                    <input
                      className="form-input"
                      type="email"
                      name="email"
                      value={account.email || ''}
                      onChange={handleChange}
                      placeholder="e.g. john@email.com"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>

              {/* ── Social ── */}
              <div className="form-section-label" style={{ marginTop: 8 }}>Social</div>
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-send" />
                      Telegram
                    </label>
                    <div className="form-input-group">
                      <span className="form-input-prefix">@</span>
                      <input
                        className="form-input"
                        type="text"
                        name="telegram"
                        value={account.telegram || ''}
                        onChange={handleChange}
                        placeholder="username"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-hash" />
                      Telegram ID
                    </label>
                    <input
                      className="form-input"
                      type="text"
                      name="telegramID"
                      value={account.telegramID || ''}
                      onChange={handleChange}
                      placeholder="e.g. 123456789"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-discord" />
                      Discord Username
                    </label>
                    <input
                      className="form-input"
                      type="text"
                      name="discord"
                      value={account.discord || ''}
                      onChange={handleChange}
                      placeholder="e.g. johndoe"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-hash" />
                      Discord ID
                    </label>
                    <input
                      className="form-input"
                      type="text"
                      name="discordID"
                      value={account.discordID || ''}
                      onChange={handleChange}
                      placeholder="e.g. 123456789012345678"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-chat-left-dots" />
                      Private Channel ID
                    </label>
                    <input
                      className="form-input"
                      type="text"
                      name="discordPrivateChannelID"
                      value={account.discordPrivateChannelID || ''}
                      onChange={handleChange}
                      placeholder="e.g. 987654321098765432"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>

              {/* ── Account type ── */}
              <div className="form-section-label" style={{ marginTop: 8 }}>Account Type</div>
              <div className="row g-4">
                <div className="col-12">
                  <label className="form-field-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={account.accountType === 'reseller'}
                      onChange={e => setAccount(prev => ({ ...prev, accountType: e.target.checked ? 'reseller' : 'customer' }))}
                    />
                    This is a reseller account
                  </label>
                  <div style={{ marginTop: 6, fontSize: '0.78rem', color: 'var(--color-muted)' }}>
                    Resellers buy credits in bulk rather than holding subscriptions. Switching an
                    account over changes which tabs its page shows; nothing already recorded is removed.
                  </div>
                </div>
              </div>

              {/* ── Dates ── */}
              <div className="form-section-label" style={{ marginTop: 8 }}>Dates</div>
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-calendar-plus" />
                      Created Date
                      <span className="required-star">*</span>
                    </label>
                    <input
                      className="form-input"
                      type="date"
                      name="createdDate"
                      value={formatDateForInput(account.createdDate)}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-field">
                    <label className="form-field-label">
                      <i className="bi bi-calendar-check" />
                      Last Updated
                    </label>
                    <input
                      className="form-input"
                      type="text"
                      value={account.updatedDate ? new Date(account.updatedDate).toLocaleString() : '—'}
                      readOnly
                      style={{ color: '#475569', cursor: 'default' }}
                    />
                  </div>
                </div>
              </div>

              {/* Feedback */}
              {updated && (
                <div className="form-banner success">
                  <i className="bi bi-check-circle-fill" />
                  Account updated successfully!
                </div>
              )}
              {error && (
                <div className="form-banner error">
                  <i className="bi bi-exclamation-triangle-fill" />
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-form-cancel"
                  onClick={() => window.history.back()}
                >
                  <i className="bi bi-x-lg" />
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-form-submit"
                  disabled={submitting}
                >
                  <i className={`bi ${submitting ? 'bi-hourglass-split' : 'bi-check-lg'}`} />
                  {submitting ? 'Saving…' : 'Save Changes'}
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </>
  );
}
